<template>
	<div class="tsc-root">
		<div v-if="series.labels.length > 0" class="tsc-chart-wrap">
			<Line :data="chartData" :options="chartOptions" />
		</div>
		<div v-else class="tsc-empty">No mosquitoes captured.</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
	Chart as ChartJS,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";
import type { ChartData, ChartOptions } from "chart.js";

import { SPECIES_PALETTE, buildStationCountsSeries } from "./trapStationCounts";
import type { MapReviewTrapInstanceRow } from "@/type/api";

ChartJS.register(
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
);

const props = defineProps<{ rows: MapReviewTrapInstanceRow[] }>();

const series = computed(() => buildStationCountsSeries(props.rows));

const chartData = computed<ChartData<"line">>(() => ({
	labels: series.value.labels,
	datasets: series.value.datasets.map((dataset, index) => {
		const color = SPECIES_PALETTE[index % SPECIES_PALETTE.length];
		return {
			label: dataset.label,
			data: dataset.data,
			borderColor: color,
			backgroundColor: color,
			borderWidth: 2,
			tension: 0.25,
			pointRadius: 2,
			pointHoverRadius: 4,
			fill: false,
		};
	}),
}));

const chartOptions: ChartOptions<"line"> = {
	responsive: true,
	maintainAspectRatio: false,
	interaction: { mode: "index", intersect: false },
	plugins: {
		legend: {
			position: "bottom",
			labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 } },
		},
		tooltip: { enabled: true },
	},
	scales: {
		x: {
			grid: { display: false },
			ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true },
		},
		y: {
			beginAtZero: true,
			ticks: { precision: 0, font: { size: 10 } },
		},
	},
};
</script>

<style scoped>
.tsc-root {
	min-width: 0;
}
.tsc-chart-wrap {
	position: relative;
	height: 160px;
}
/* The popup's empty-state voice: italic, quiet, one step down. Scoped to this
   component, since the popup's own `.popup-empty` does not reach inside it. */
.tsc-empty {
	font-size: var(--pp-text-xs);
	font-style: italic;
	color: var(--pp-ink-3);
	line-height: 1.45;
}
</style>
